/**
 * PDP behavior — ports of ProductInfoPanel / ProductGallery / ProductAccordion /
 * QuantitySelector / DeliveryEstimate client logic:
 * - variant tiles: swap subtitle / price / CTA price / stock / SKU, 2px accent
 *   border on the active tile, ?variant= via replaceState (no navigation)
 * - quantity: min 1, hidden input syncs into the product form
 * - add to cart: /cart/add.js → refresh /cart.js → document event
 *   `dylys:cart-change` (cart drawer + gift bar subscribe); Shopify error
 *   descriptions surface in the panel
 * - gallery: image-load scale 1.2→1 (1200ms ease-out), dot-rail scroll-spy
 *   (IO rootMargin -35%/-45%, thresholds .2/.45/.7), smooth scroll
 * - lightbox: scroll-snap slide track (swipe on touch, arrows + ←/→ on
 *   desktop), counter, Esc / SCHLIESSEN / backdrop close. Opening pushes a
 *   history entry so the iOS back swipe / Android back closes the lightbox
 *   first and only the next one leaves the page (popstate)
 * - mobile (below lg, Prada-style): the gallery is a sticky box whose grid
 *   scrolls vertically with snap (dots = pagination, the lightbox hands its
 *   position back on close); the panel is a sheet that slides over it when
 *   the page scrolls. Its peek (product-bar: serie/title/price/swatches +
 *   direct add, shares the variant state, submits the panel form) is measured
 *   into --pdp-peek, the header height into --pdp-header; every scroll that
 *   ends between "collapsed" (0) and "expanded" (sheet under the header)
 *   snaps to one of the two in the direction of the last movement
 * - delivery estimate: cutoff-aware ship day (holiday-aware business days)
 *   + transit business days, de-DE Intl range format
 * - gift bar (D8): cart-driven, updates on every cart mutation
 * - accordion: single-open, +/- indicator, no animation
 */
(function () {
  'use strict';

  // ── Money (matches the shop format "1.234,56 €") ───────────────────────────
  function formatMoney(cents) {
    var amount = (cents / 100).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return amount + ' €';
  }

  // ── Delivery estimate ──────────────────────────────────────────────────────
  // Non-binding range ("Voraussichtliche Lieferung"), computed client-side
  // because Liquid's `now` is CDN-cached. Ship day: today (shop timezone) on
  // business days before the cutoff, otherwise the next business day. Range =
  // ship day + min..max transit business days. Business days are Mon–Fr minus
  // the holidays configured in snippets/delivery-estimate.liquid
  // (docs/DELIVERY-LOGIC.md).
  function isoKey(d) {
    var mo = d.getMonth() + 1;
    var da = d.getDate();
    return d.getFullYear() + '-' + (mo < 10 ? '0' : '') + mo + '-' + (da < 10 ? '0' : '') + da;
  }

  function isBusinessDay(d, holidays) {
    var weekday = d.getDay();
    return weekday !== 0 && weekday !== 6 && !holidays[isoKey(d)];
  }

  function addBusinessDays(start, days, holidays) {
    var d = new Date(start);
    var added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      if (isBusinessDay(d, holidays)) added++;
    }
    return d;
  }

  function shopNow(tz) {
    try {
      var parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', hour12: false
      }).formatToParts(new Date());
      var get = function (type) {
        var p = parts.filter(function (x) { return x.type === type; })[0];
        return p ? parseInt(p.value, 10) : NaN;
      };
      var y = get('year'), mo = get('month'), da = get('day'), h = get('hour');
      if (!isNaN(y) && !isNaN(mo) && !isNaN(da)) {
        return { date: new Date(y, mo - 1, da), hour: isNaN(h) ? 12 : (h === 24 ? 0 : h) };
      }
    } catch (e) { /* fall through */ }
    var n = new Date();
    return { date: new Date(n.getFullYear(), n.getMonth(), n.getDate()), hour: n.getHours() };
  }

  function formatDeliveryRange(from, to) {
    var weekday = function (d) {
      return new Intl.DateTimeFormat('de-DE', { weekday: 'short' }).format(d).replace('.', '');
    };
    var month = function (d) {
      return new Intl.DateTimeFormat('de-DE', { month: 'long' }).format(d);
    };
    if (from.getTime() === to.getTime()) {
      return weekday(from) + '., ' + from.getDate() + '. ' + month(from);
    }
    var sameMonth = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear();
    if (sameMonth) {
      return weekday(from) + '., ' + from.getDate() + '. – ' + weekday(to) + '., ' + to.getDate() + '. ' + month(to);
    }
    return weekday(from) + '., ' + from.getDate() + '. ' + month(from) + ' – ' + weekday(to) + '., ' + to.getDate() + '. ' + month(to);
  }

  function initDeliveryEstimates(root) {
    (root || document).querySelectorAll('[data-delivery-estimate]').forEach(function (box) {
      if (box.dataset.deliveryInit) return;
      box.dataset.deliveryInit = '1';
      var min = parseInt(box.getAttribute('data-delivery-min'), 10) || 3;
      var max = parseInt(box.getAttribute('data-delivery-max'), 10) || 5;
      var cutoff = parseInt(box.getAttribute('data-delivery-cutoff'), 10) || 0;
      var tz = box.getAttribute('data-delivery-tz') || 'Europe/Copenhagen';
      var holidays = {};
      (box.getAttribute('data-delivery-holidays') || '').split(',').forEach(function (iso) {
        iso = iso.trim();
        if (iso) holidays[iso] = true;
      });

      var now = shopNow(tz);
      var ship = now.date;
      if (!isBusinessDay(ship, holidays) || (cutoff > 0 && now.hour >= cutoff)) {
        ship = addBusinessDays(ship, 1, holidays);
      }
      var shipsToday = ship.getTime() === now.date.getTime();

      var shipEl = box.querySelector('[data-delivery-ship]');
      if (shipEl) {
        shipEl.textContent = shipsToday
          ? shipEl.getAttribute('data-ship-today-text')
          : shipEl.getAttribute('data-ship-next-text');
        shipEl.hidden = false;
      }

      var rangeEl = box.querySelector('[data-delivery-range]');
      if (rangeEl) {
        rangeEl.textContent = formatDeliveryRange(
          addBusinessDays(ship, min, holidays),
          addBusinessDays(ship, max, holidays)
        );
      }
    });
  }

  // ── Accordion ──────────────────────────────────────────────────────────────
  function initAccordions(root) {
    (root || document).querySelectorAll('[data-accordion]').forEach(function (acc) {
      if (acc.dataset.accordionInit) return;
      acc.dataset.accordionInit = '1';

      var toggles = Array.prototype.slice.call(acc.querySelectorAll('[data-accordion-toggle]'));

      toggles.forEach(function (toggle) {
        toggle.addEventListener('click', function () {
          var wasOpen = toggle.getAttribute('aria-expanded') === 'true';
          toggles.forEach(function (other) {
            other.setAttribute('aria-expanded', 'false');
            other.querySelector('.accordion__indicator').textContent = '+';
            var content = other.parentElement.querySelector('[data-accordion-content]');
            if (content) content.hidden = true;
          });
          if (!wasOpen) {
            toggle.setAttribute('aria-expanded', 'true');
            toggle.querySelector('.accordion__indicator').textContent = '-';
            var openContent = toggle.parentElement.querySelector('[data-accordion-content]');
            if (openContent) openContent.hidden = false;
          }
        });
      });
    });
  }

  // ── Gallery ────────────────────────────────────────────────────────────────
  function initGallery(gallery) {
    if (gallery.dataset.galleryInit) return;
    gallery.dataset.galleryInit = '1';

    // Image-load reveal: scale 1.2 → 1 (1200ms ease-out)
    gallery.querySelectorAll('.product-gallery__img').forEach(function (img) {
      var reveal = function () { img.classList.add('is-loaded'); };
      if (img.complete && img.naturalWidth > 0) {
        reveal();
      } else {
        img.addEventListener('load', reveal, { once: true });
        img.addEventListener('error', reveal, { once: true });
      }
    });

    var tiles = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-tile]'));
    var dots = Array.prototype.slice.call(gallery.querySelectorAll('[data-gallery-dot]'));

    // Mobile (below lg): the grid is a vertical scroll-snap scroller inside
    // the sticky gallery box behind the product sheet — the same dots act as
    // its pagination. Detected via the computed overflow so the breakpoint
    // lives in CSS only.
    var strip = gallery.querySelector('[data-gallery-track]');
    var isScroller = function () {
      return !!strip && getComputedStyle(strip).overflowY === 'auto';
    };
    var visibleTiles = function () {
      return tiles.filter(function (tile) { return !tile.classList.contains('is-hidden'); });
    };
    var setActiveDot = function (index) {
      dots.forEach(function (dot) {
        dot.classList.toggle('is-active', dot.getAttribute('data-gallery-dot') === String(index));
      });
    };
    var stripIndex = function () {
      if (!strip || !strip.clientHeight) return 0;
      return Math.round(strip.scrollTop / strip.clientHeight);
    };
    var scrollStripTo = function (tile, behavior) {
      if (!strip || !tile) return;
      strip.scrollTo({ top: tile.offsetTop, behavior: behavior || 'auto' });
    };
    var syncStrip = function () {
      if (!isScroller()) return;
      var visible = visibleTiles();
      var index = Math.min(visible.length - 1, Math.max(0, stripIndex()));
      if (visible[index]) setActiveDot(visible[index].getAttribute('data-gallery-tile'));
    };

    if (strip) {
      var stripTicking = false;
      strip.addEventListener('scroll', function () {
        if (stripTicking) return;
        stripTicking = true;
        window.requestAnimationFrame(function () {
          stripTicking = false;
          syncStrip();
        });
      }, { passive: true });
      // colour switch (filterGallery below) → back to the first visible slide
      gallery.addEventListener('dylys:gallery-filter', function () {
        if (!isScroller()) return;
        strip.scrollTo({ top: 0 });
        syncStrip();
      });
      window.addEventListener('resize', syncStrip);
      syncStrip();
    }

    // Desktop scroll-spy — most-visible tile wins
    if (tiles.length && dots.length) {
      var observer = new IntersectionObserver(function (entries) {
        if (isScroller()) return;
        var visible = entries
          .filter(function (entry) { return entry.isIntersecting; })
          .sort(function (a, b) { return b.intersectionRatio - a.intersectionRatio; })[0];
        if (visible) setActiveDot(visible.target.getAttribute('data-gallery-tile'));
      }, { rootMargin: '-35% 0px -45% 0px', threshold: [0.2, 0.45, 0.7] });
      tiles.forEach(function (tile) { observer.observe(tile); });

      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          var index = dot.getAttribute('data-gallery-dot');
          var target = gallery.querySelector('[data-gallery-tile="' + index + '"]');
          if (!target) return;
          if (isScroller()) scrollStripTo(target, 'smooth');
          else target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }

    // Lightbox — swipeable slide track + history-backed close. Lives next to
    // the gallery (not inside: the sticky mobile gallery is a stacking context).
    var lightbox = gallery.querySelector('[data-gallery-lightbox]') ||
      (gallery.parentElement && gallery.parentElement.querySelector('[data-gallery-lightbox]'));
    if (lightbox) {
      var track = lightbox.querySelector('[data-lightbox-track]');
      var slides = Array.prototype.slice.call(lightbox.querySelectorAll('[data-lightbox-slide]'));
      var counter = lightbox.querySelector('[data-lightbox-counter]');
      var prevBtn = lightbox.querySelector('[data-lightbox-prev]');
      var nextBtn = lightbox.querySelector('[data-lightbox-next]');
      var isOpen = false;
      var pushedState = false;

      var visibleSlides = function () {
        return slides.filter(function (slide) { return !slide.classList.contains('is-hidden'); });
      };
      var currentIndex = function () {
        if (!track || !track.clientWidth) return 0;
        return Math.round(track.scrollLeft / track.clientWidth);
      };
      var updateCounter = function () {
        var visible = visibleSlides();
        var index = Math.min(visible.length - 1, Math.max(0, currentIndex()));
        if (counter) counter.textContent = visible.length ? (index + 1) + ' / ' + visible.length : '';
        if (prevBtn) prevBtn.disabled = index <= 0;
        if (nextBtn) nextBtn.disabled = index >= visible.length - 1;
      };
      var scrollToSlide = function (slide, behavior) {
        if (!track || !slide) return;
        track.scrollTo({ left: slide.offsetLeft, behavior: behavior || 'auto' });
        updateCounter();
      };
      var step = function (delta) {
        var visible = visibleSlides();
        var target = visible[Math.min(visible.length - 1, Math.max(0, currentIndex() + delta))];
        scrollToSlide(target, 'smooth');
      };

      // hide = DOM only; close = user-initiated (also pops our history entry)
      var hide = function () {
        if (!isOpen) return;
        isOpen = false;
        // keep the mobile slider on the image the visitor swiped to
        var openSlides = visibleSlides();
        var current = openSlides[Math.min(openSlides.length - 1, Math.max(0, currentIndex()))];
        if (current && isScroller()) {
          scrollStripTo(gallery.querySelector('[data-gallery-tile="' + current.getAttribute('data-lightbox-slide') + '"]'), 'auto');
        }
        lightbox.hidden = true;
        window.Dylys.unlockScroll();
      };
      var close = function () {
        if (!isOpen) return;
        hide();
        if (pushedState) {
          pushedState = false;
          try { history.back(); } catch (e) { /* ignore */ }
        }
      };
      var open = function (mediaIndex) {
        lightbox.hidden = false;
        isOpen = true;
        window.Dylys.lockScroll();
        scrollToSlide(track ? track.querySelector('[data-lightbox-slide="' + mediaIndex + '"]') : null, 'auto');
        if (!pushedState) {
          try {
            history.pushState({ dylysLightbox: true }, '', window.location.href);
            pushedState = true;
          } catch (e) { pushedState = false; }
        }
      };

      tiles.forEach(function (tile) {
        tile.addEventListener('click', function () {
          open(tile.getAttribute('data-gallery-tile'));
        });
      });

      if (track) {
        var ticking = false;
        track.addEventListener('scroll', function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(function () {
            ticking = false;
            updateCounter();
          });
        }, { passive: true });
        // Tap anywhere beside the picture closes. The <img> box fills the whole
        // slide (object-fit: contain), so a tap in its letterbox area must count
        // as "outside the picture" too — only the rendered picture itself keeps
        // the lightbox open.
        var onPicture = function (img, x, y) {
          var rect = img.getBoundingClientRect();
          var nw = img.naturalWidth || parseFloat(img.getAttribute('width')) || 0;
          var nh = img.naturalHeight || parseFloat(img.getAttribute('height')) || 0;
          if (!nw || !nh || !rect.width || !rect.height) return true;
          var scale = Math.min(rect.width / nw, rect.height / nh);
          var w = nw * scale;
          var h = nh * scale;
          var left = rect.left + (rect.width - w) / 2;
          var top = rect.top + (rect.height - h) / 2;
          return x >= left && x <= left + w && y >= top && y <= top + h;
        };
        track.addEventListener('click', function (e) {
          var img = e.target.closest('.product-gallery__lightbox-img');
          if (!img || !onPicture(img, e.clientX, e.clientY)) close();
        });
      }
      if (prevBtn) prevBtn.addEventListener('click', function () { step(-1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { step(1); });
      var closeBtn = lightbox.querySelector('[data-lightbox-close]');
      if (closeBtn) closeBtn.addEventListener('click', close);

      window.addEventListener('keydown', function (e) {
        if (!isOpen) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowLeft') step(-1);
        else if (e.key === 'ArrowRight') step(1);
      });

      // Back gesture / back button: first press closes the lightbox only.
      window.addEventListener('popstate', function () {
        if (isOpen) {
          pushedState = false;
          hide();
        }
      });
      // A stale lightbox entry (e.g. bfcache restore) must not sit on the stack.
      if (history.state && history.state.dylysLightbox) {
        try { history.replaceState(null, '', window.location.href); } catch (e) { /* ignore */ }
      }
    }
  }

  // ── Product panel ──────────────────────────────────────────────────────────
  function initProductPage(page) {
    if (page.dataset.productInit) return;
    page.dataset.productInit = '1';

    var gallery = page.querySelector('[data-product-gallery]');
    if (gallery) initGallery(gallery);
    initDeliveryEstimates(page);
    initAccordions(page);

    var variantScript = page.parentElement.querySelector('[data-variant-data]') || document.querySelector('[data-variant-data]');
    var variants = [];
    try {
      variants = JSON.parse(variantScript ? variantScript.textContent : '[]');
    } catch (e) { /* leave empty */ }

    var subtitle = page.querySelector('[data-product-subtitle]');
    var price = page.querySelector('[data-product-price]');
    var stock = page.querySelector('[data-product-stock]');
    var ctaPrice = page.querySelector('[data-cta-price]');
    var atcBtn = page.querySelector('[data-add-to-cart]');
    var idInput = page.querySelector('[data-variant-id-input]');
    var skuLine = page.querySelector('[data-accordion-sku]');
    var errorEl = page.querySelector('[data-cart-error]');

    var priceCompare = page.querySelector('[data-product-price-compare]');

    // Mobile sheet peek (snippets/product-bar.liquid)
    var bar = page.querySelector('[data-product-bar]');
    var barPrice = bar ? bar.querySelector('[data-bar-price]') : null;
    var barCompare = bar ? bar.querySelector('[data-bar-price-compare]') : null;
    var barVariant = bar ? bar.querySelector('[data-bar-variant]') : null;
    var barAtc = bar ? bar.querySelector('[data-bar-atc]') : null;
    var barError = bar ? bar.querySelector('[data-bar-error]') : null;

    var currentVariant = variants.filter(function (v) {
      return idInput && String(v.id) === String(idInput.value);
    })[0] || null;
    var initialCanBuy = atcBtn ? !atcBtn.disabled : true;
    var canBuy = function () { return currentVariant ? !!currentVariant.available : initialCanBuy; };

    // Gallery color filter (F30): show only the selected variant's media group
    // (+ 'shared' files without an EAN filename); if the variant owns no media,
    // show everything.
    function filterGallery(variantId, hasMedia) {
      if (!gallery) return;
      var group = hasMedia ? String(variantId) : 'all';
      gallery.setAttribute('data-active-group', group);
      // tiles + dots in the gallery, slides in the lightbox beside it
      page.querySelectorAll('[data-media-group]').forEach(function (el) {
        var g = el.getAttribute('data-media-group');
        el.classList.toggle('is-hidden', group !== 'all' && g !== group && g !== 'shared');
      });
      gallery.dispatchEvent(new CustomEvent('dylys:gallery-filter', { detail: { group: group } }));
    }

    // Variant selection — one path for the panel tiles and the sheet swatches
    function selectVariant(variant) {
      currentVariant = variant;
      var idStr = String(variant.id);

      page.querySelectorAll('[data-variant-tile]').forEach(function (tile) {
        tile.classList.toggle('is-active', tile.getAttribute('data-variant-tile') === idStr);
      });
      page.querySelectorAll('[data-bar-swatch]').forEach(function (swatch) {
        var on = swatch.getAttribute('data-bar-swatch') === idStr;
        swatch.classList.toggle('is-active', on);
        swatch.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      if (idInput) idInput.value = variant.id;
      if (subtitle) subtitle.textContent = variant.title;
      if (price) price.textContent = variant.price;
      if (priceCompare) {
        if (variant.compare_at) {
          priceCompare.textContent = variant.compare_at;
          priceCompare.hidden = false;
        } else {
          priceCompare.textContent = '';
          priceCompare.hidden = true;
        }
      }
      if (stock) stock.textContent = variant.stock;
      if (skuLine && variant.sku) {
        skuLine.textContent = skuLine.textContent.split(':')[0] + ': ' + variant.sku;
      }
      if (atcBtn) {
        atcBtn.disabled = !variant.available;
        if (variant.available && ctaPrice) ctaPrice.textContent = variant.cta_price;
      }

      if (barPrice) barPrice.textContent = variant.price;
      if (barCompare) {
        barCompare.textContent = variant.compare_at || '';
        barCompare.hidden = !variant.compare_at;
      }
      if (barVariant) barVariant.textContent = variant.title;
      if (barAtc) {
        barAtc.disabled = !variant.available;
        barAtc.textContent = variant.available
          ? barAtc.getAttribute('data-label-add')
          : barAtc.getAttribute('data-label-sold');
      }

      filterGallery(variant.id, variant.has_media);

      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());
    }

    page.querySelectorAll('[data-variant-tile], [data-bar-swatch]').forEach(function (el) {
      el.addEventListener('click', function () {
        var id = el.getAttribute('data-variant-tile') || el.getAttribute('data-bar-swatch');
        var variant = variants.filter(function (v) { return String(v.id) === id; })[0];
        if (variant) selectVariant(variant);
      });
    });

    // Product sheet (below lg): .product-panel slides over the sticky gallery
    // as the page scrolls. The peek height feeds the gallery height
    // (--pdp-peek), the header height the sticky offset (--pdp-header). Every
    // scroll that ends between "collapsed" (0) and "expanded" (sheet docked
    // under the header) snaps to one of the two, following the last movement,
    // once the finger is off the screen. Below lg only — detected through the
    // gallery's computed position so the breakpoint lives in CSS.
    var sheet = page.querySelector('[data-product-sheet]');
    var peek = page.querySelector('[data-sheet-peek]');
    var sheetToggle = page.querySelector('[data-sheet-toggle]');
    if (sheet && peek && gallery) {
      var root = document.documentElement;
      var siteHeader = document.querySelector('[data-site-header]');
      root.classList.add('has-product-sheet');

      var sheetMode = false;
      var updateSheetMode = function () {
        sheetMode = getComputedStyle(gallery).position === 'sticky';
      };
      var setSizes = function () {
        root.style.setProperty('--pdp-peek', peek.offsetHeight + 'px');
        if (siteHeader) root.style.setProperty('--pdp-header', siteHeader.offsetHeight + 'px');
      };
      var stickyTop = function () {
        return parseFloat(getComputedStyle(gallery).top) || 0;
      };
      var expandedY = function () {
        return Math.round(sheet.getBoundingClientRect().top + window.scrollY - stickyTop());
      };
      var atRest = function (y, top) { return y <= 1 || y >= top - 1; };
      var snapUntil = 0;
      var goTo = function (y) {
        snapUntil = Date.now() + 900; // scroll listener: don't re-settle while animating
        window.scrollTo({ top: y, behavior: 'smooth' });
      };
      var syncToggle = function () {
        if (sheetToggle) {
          sheetToggle.setAttribute('aria-expanded', window.scrollY >= expandedY() - 1 ? 'true' : 'false');
        }
      };

      updateSheetMode();
      setSizes();
      if ('ResizeObserver' in window) {
        var sizeObserver = new ResizeObserver(setSizes);
        sizeObserver.observe(peek);
        if (siteHeader) sizeObserver.observe(siteHeader);
      }
      window.addEventListener('resize', function () {
        updateSheetMode();
        setSizes();
      });

      if (sheetToggle) {
        sheetToggle.addEventListener('click', function () {
          if (!sheetMode) return;
          var top = expandedY();
          goTo(window.scrollY < top / 2 ? top : 0);
        });
      }

      var lastY = window.scrollY;
      var dir = 0;
      var touching = false;
      var settleTimer = null;
      var settle = function () {
        if (!sheetMode || touching) return;
        var y = window.scrollY;
        var top = expandedY();
        syncToggle();
        if (atRest(y, top)) return;
        goTo(dir < 0 ? 0 : (dir > 0 ? top : (y > top / 2 ? top : 0)));
      };
      var schedule = function () {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(settle, 120);
      };
      window.addEventListener('scroll', function () {
        if (!sheetMode) return;
        var y = window.scrollY;
        if (y !== lastY) dir = y > lastY ? 1 : -1;
        lastY = y;
        if (snapUntil) {
          if (atRest(y, expandedY()) || Date.now() > snapUntil) {
            snapUntil = 0;
            syncToggle();
          }
          return;
        }
        schedule();
      }, { passive: true });
      window.addEventListener('touchstart', function () {
        touching = true;
        snapUntil = 0;
        clearTimeout(settleTimer);
      }, { passive: true });
      var touchDone = function () {
        touching = false;
        schedule();
      };
      window.addEventListener('touchend', touchDone, { passive: true });
      window.addEventListener('touchcancel', touchDone, { passive: true });

      // Swipe up on the LAST image → the sheet comes up right away. iOS keeps
      // the gesture inside the snap scroller instead of chaining it to the
      // page (and the strip has overscroll-behavior: contain so no browser
      // double-handles it): we read the gesture ourselves.
      var strip = gallery.querySelector('[data-gallery-track]');
      if (strip) {
        var swipeStartY = null;
        var swipeAtEnd = false;
        var stripAtEnd = function () {
          return strip.scrollTop >= strip.scrollHeight - strip.clientHeight - 2;
        };
        strip.addEventListener('touchstart', function (e) {
          if (!sheetMode || e.touches.length !== 1) { swipeStartY = null; return; }
          swipeStartY = e.touches[0].clientY;
          swipeAtEnd = stripAtEnd();
        }, { passive: true });
        strip.addEventListener('touchmove', function (e) {
          if (swipeStartY === null || !swipeAtEnd) return;
          if (swipeStartY - e.touches[0].clientY < 24) return;
          swipeStartY = null; // once per gesture
          if (window.scrollY < expandedY() - 1) goTo(expandedY());
        }, { passive: true });
      }
      schedule();
    }

    // Quantity
    var qty = page.querySelector('[data-quantity]');
    if (qty) {
      var value = qty.querySelector('[data-quantity-value]');
      var input = qty.querySelector('[data-quantity-input]');
      var setQty = function (n) {
        n = Math.max(1, n);
        if (value) value.textContent = n;
        if (input) input.value = n;
      };
      var read = function () { return parseInt(value ? value.textContent : '1', 10) || 1; };
      qty.querySelector('[data-quantity-decrease]').addEventListener('click', function () { setQty(read() - 1); });
      qty.querySelector('[data-quantity-increase]').addEventListener('click', function () { setQty(read() + 1); });
    }

    // Add to cart
    var form = page.querySelector('[data-product-form]');
    if (form) {
      var setBusy = function (busy) {
        var disabled = busy || !canBuy();
        if (atcBtn) atcBtn.disabled = disabled;
        if (barAtc) barAtc.disabled = disabled;
      };
      var showError = function (message) {
        [errorEl, barError].forEach(function (el) {
          if (!el) return;
          el.textContent = message || (errorEl && errorEl.getAttribute('data-fallback')) || '';
          el.hidden = false;
        });
      };

      if (barAtc) {
        barAtc.addEventListener('click', function () {
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        });
      }

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (errorEl) errorEl.hidden = true;
        if (barError) barError.hidden = true;
        setBusy(true);

        var formData = new FormData(form);
        fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: formData
        })
          .then(function (res) {
            return res.json().then(function (data) { return { ok: res.ok, data: data }; });
          })
          .then(function (result) {
            if (!result.ok) {
              throw new Error(result.data && (result.data.description || result.data.message));
            }
            return fetch('/cart.js').then(function (res) { return res.json(); });
          })
          .then(function (cart) {
            setBusy(false);
            document.dispatchEvent(new CustomEvent('dylys:cart-change', {
              detail: { cart: cart, opened: true }
            }));
          })
          .catch(function (err) {
            setBusy(false);
            showError(err && err.message);
          });
      });
    }
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-product-page]').forEach(initProductPage);
    initDeliveryEstimates(root);
    initAccordions(root);
  }

  initAll(document);

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  window.Dylys = window.Dylys || {};
  window.Dylys.formatMoney = formatMoney;
})();
